/**
 * TestimonialsAdminPage — standalone admin page for managing testimonials.
 * Extracted from ControlPanel to give Testimonials its own admin route.
 */
import {
  RefreshCw,
  MessageSquare,
  AlertCircle,
  CheckCircle2,
  Trash2,
  Star,
  Filter,
  ArrowUpDown,
  Plus,
  X,
} from 'lucide-react';
import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { DataTable } from '../../components/DataTable';
import type { Column } from '../../components/DataTable';
import { useAuthentication } from '../../hooks/useAuthentication';
import { supabase } from '../../lib/supabase';
import { isOwnerAdminEmail } from '../../modules/dashboard/components/Jobs/helpers';
import Layout from '../../modules/dashboard/components/Layout';

interface Testimonial extends Record<string, unknown> {
  id:                string;
  user_id:           string;
  quote:             string;
  user_display_name: string;
  role_landed:       string;
  company_name:      string;
  rating:            number | null;
  approved:          boolean;
  created_at:        string;
}

const RATING_OPTIONS = [4.5, 4, 3.5] as const;

const MIN_QUOTE_LEN = 20;
const MAX_QUOTE_LEN = 500;

function fmtDate(v: string | null | undefined): string {
  if (!v) return '—';
  try { return new Date(v).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }); }
  catch { return '—'; }
}

interface AddTestimonialForm {
  userDisplayName: string;
  quote: string;
  roleLanded: string;
  companyName: string;
  rating: number;
  approveImmediately: boolean;
}

const EMPTY_ADD_FORM: AddTestimonialForm = {
  userDisplayName: '',
  quote: '',
  roleLanded: '',
  companyName: '',
  rating: 4.5,
  approveImmediately: false,
};

interface TestimonialsAdminPageProps {
  onSignOut?: () => void | Promise<void>;
  /** When true the component renders its content directly without the Layout shell */
  embedded?: boolean;
}

export default function TestimonialsAdminPage({ onSignOut = () => {}, embedded = false }: TestimonialsAdminPageProps) {
  const { session, user, loading: authLoading } = useAuthentication();
  const isAdmin = useMemo(
    () => isOwnerAdminEmail(user?.email ?? session?.user?.email),
    [user?.email, session?.user?.email],
  );
  const [rows, setRows] = useState<Testimonial[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<'pending' | 'all' | 'approved'>('pending');
  const [saving, setSaving] = useState<string | null>(null);
  const [testSel, setTestSel] = useState<Set<string>>(new Set());
  const [showAddModal, setShowAddModal] = useState(false);
  const [addForm, setAddForm] = useState<AddTestimonialForm>(EMPTY_ADD_FORM);
  const [addError, setAddError] = useState<string | null>(null);

  const handleNavigate = useCallback((page: string) => {
    window.dispatchEvent(new CustomEvent('ggj:navigate', { detail: { page } }));
  }, []);

  const load = useCallback(async () => {
    if (!isAdmin || !session) return;
    setLoading(true);
    setError(null);
    try {
      const { data, error: err } = await supabase
        .from('testimonials')
        .select('*')
        .order('created_at', { ascending: false });
      if (err) throw err;
      setRows((data ?? []) as Testimonial[]);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not load testimonials.');
    } finally {
      setLoading(false);
    }
  }, [isAdmin, session]);

  useEffect(() => { void load(); }, [load]);

  const handleApprove = useCallback(async (id: string) => {
    setSaving(id);
    try {
      const { error: err } = await supabase.from('testimonials').update({ approved: true }).eq('id', id);
      if (err) throw err;
      setRows(prev => prev.map(r => r.id === id ? { ...r, approved: true } : r));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Approve failed.');
    } finally {
      setSaving(null);
    }
  }, []);

  const handleCreate = useCallback(async () => {
    const quote = addForm.quote.trim();
    const userDisplayName = addForm.userDisplayName.trim();
    const roleLanded = addForm.roleLanded.trim();
    const companyName = addForm.companyName.trim();

    if (!userDisplayName) {
      setAddError('Display name is required.');
      return;
    }
    if (quote.length < MIN_QUOTE_LEN) {
      setAddError(`Quote must be at least ${MIN_QUOTE_LEN} characters.`);
      return;
    }
    if (!roleLanded) {
      setAddError('Role is required.');
      return;
    }
    if (!companyName) {
      setAddError('Company or location is required.');
      return;
    }
    if (!session?.user?.id) {
      setAddError('You must be signed in as admin.');
      return;
    }

    setSaving('create');
    setAddError(null);
    try {
      const { data, error: err } = await supabase
        .from('testimonials')
        .insert({
          user_id: session.user.id,
          quote,
          user_display_name: userDisplayName,
          role_landed: roleLanded,
          company_name: companyName,
          rating: addForm.rating,
          approved: addForm.approveImmediately,
        })
        .select('*')
        .single();
      if (err) throw err;
      setRows((prev) => [data as Testimonial, ...prev]);
      setShowAddModal(false);
      setAddForm(EMPTY_ADD_FORM);
      if (addForm.approveImmediately) setFilter('approved');
      else setFilter('pending');
    } catch (e) {
      setAddError(e instanceof Error ? e.message : 'Could not create testimonial.');
    } finally {
      setSaving(null);
    }
  }, [addForm, session]);

  const handleReject = useCallback(async (id: string) => {
    if (!window.confirm('Delete this testimonial permanently?')) return;
    setSaving(id);
    try {
      const { error: err } = await supabase.from('testimonials').delete().eq('id', id);
      if (err) throw err;
      setRows(prev => prev.filter(r => r.id !== id));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Delete failed.');
    } finally {
      setSaving(null);
    }
  }, []);

  const visible = useMemo(() => rows.filter(r => {
    if (filter === 'pending') return !r.approved;
    if (filter === 'approved') return r.approved;
    return true;
  }), [rows, filter]);

  const pendingCount = rows.filter(r => !r.approved).length;

  const bulkApprove = async () => {
    if (!testSel.size) return;
    setSaving('bulk');
    for (const id of testSel) await supabase.from('testimonials').update({ approved: true }).eq('id', id);
    setRows(prev => prev.map(r => testSel.has(r.id) ? { ...r, approved: true } : r));
    setTestSel(new Set());
    setSaving(null);
  };

  const bulkDelete = async () => {
    if (!testSel.size) return;
    if (!window.confirm(`Delete ${testSel.size} testimonial(s) permanently?`)) return;
    setSaving('bulk');
    await supabase.from('testimonials').delete().in('id', [...testSel]);
    setRows(prev => prev.filter(r => !testSel.has(r.id)));
    setTestSel(new Set());
    setSaving(null);
  };

  const cols = useMemo<Column<Testimonial>[]>(() => [
    { key: 'user_display_name', header: 'Name', minWidth: 160, alwaysVisible: true,
      cell: (_v, row) => {
        const initials = row.user_display_name.split(' ').map(w => w[0]).slice(0, 2).join('').toUpperCase();
        const colors = ['var(--ds-brand)', 'var(--ds-info)', 'var(--ds-ok)', 'var(--ds-warn)', 'var(--ds-danger)', 'var(--ds-brand)'];
        const bg = colors[row.user_display_name.charCodeAt(0) % colors.length];
        return (
          <span className="flex items-center gap-2" onClick={e => e.stopPropagation()}>
            <input type="checkbox" checked={testSel.has(row.id)}
              onChange={() => setTestSel(prev => { const n = new Set(prev); n.has(row.id) ? n.delete(row.id) : n.add(row.id); return n; })}
              onClick={e => e.stopPropagation()}
              className="shrink-0 w-3.5 h-3.5 rounded accent-purple-600 cursor-pointer" />
            <span className="shrink-0 w-8 h-8 rounded-full flex items-center justify-center t-tag shadow-sm"
              style={{ background: bg, color: 'var(--ds-text-on-brand)' }}>{initials}</span>
            <span className="min-w-0">
              <span className="block truncate t-card-title">
                {row.user_display_name}
              </span>
            </span>
          </span>
        );
      },
    },
    { key: 'quote', header: 'Testimonial', minWidth: 300,
      cell: (v) => (
        <span className="t-meta line-clamp-2">
          &ldquo;{String(v).slice(0, 150)}{String(v).length > 150 ? '…' : ''}&rdquo;
        </span>
      ),
    },
    { key: 'role_landed', header: 'Role', minWidth: 140,
      cell: (_v, row) => (
        <span className="min-w-0">
          <span className="block truncate t-card-title">
            {row.role_landed}
          </span>
          <span className="block truncate t-tag t-muted">
            @ {row.company_name}
          </span>
        </span>
      ),
    },
    { key: 'rating', header: 'Rating', minWidth: 90,
      cell: (v) => {
        const rating = v != null ? Number(v) : null;
        if (!rating) return <span className="t-meta">—</span>;
        return (
          <span className="inline-flex items-center gap-1 t-meta">
            <Star className="w-3.5 h-3.5 fill-amber-400 text-amber-400" aria-hidden />
            {rating % 1 === 0 ? rating.toFixed(0) : rating.toFixed(1)}
          </span>
        );
      },
    },
    { key: 'approved', header: 'Status', minWidth: 100,
      cell: (v) => v ? (
        <span className="ds-chip ds-chip-ok">
          <Star className="w-3 h-3 fill-emerald-600" />Live
        </span>
      ) : (
        <span className="ds-chip ds-chip-warn">
          Pending
        </span>
      ),
    },
    { key: 'created_at', header: 'Submitted', minWidth: 120,
      cell: (v) => <span className="t-meta">{fmtDate(v as string)}</span>,
    },
    { key: 'id', header: 'Actions', minWidth: 160, sortable: false,
      cell: (_v, row) => (
        <div className="flex items-center gap-1.5" onClick={e => e.stopPropagation()}>
          {!row.approved && (
            <button type="button" onClick={() => void handleApprove(row.id)} disabled={!!saving}
              className="ds-btn ds-btn-ghost ds-btn-sm" style={{ color: 'var(--ds-ok)' }}>
              <CheckCircle2 className="w-3.5 h-3.5" />
              {saving === row.id ? '…' : 'Approve'}
            </button>
          )}
          <button type="button" onClick={() => void handleReject(row.id)} disabled={!!saving}
            className="ds-btn ds-btn-ghost ds-btn-sm" style={{ color: 'var(--ds-danger)' }}>
            <Trash2 className="w-3.5 h-3.5" />
            {saving === row.id ? '…' : row.approved ? 'Remove' : 'Reject'}
          </button>
        </div>
      ),
    },
  ], [testSel, saving, handleApprove, handleReject]);

  const loadingSpinner = (
    <div className="flex items-center justify-center min-h-[40vh]">
      <div className="w-6 h-6 border-2 border-t-transparent rounded-full animate-spin"
        style={{ borderColor: 'var(--ds-brand-border)', borderTopColor: 'var(--ds-brand)' }} />
    </div>
  );

  if (authLoading) return embedded ? loadingSpinner : (
    <Layout currentPage="testimonials-admin" onNavigate={handleNavigate} onSignOut={onSignOut}>
      {loadingSpinner}
    </Layout>
  );

  const content = (
    <div className="ds-page px-2 sm:px-4 lg:px-6 py-4 sm:py-6 max-w-[1440px] mx-auto w-full min-w-0 space-y-4">
        {/* Page header */}
        <div className="flex items-center gap-3">
          <div className="ds-icon-square ds-icon-square-lg shrink-0">
            <MessageSquare className="w-5 h-5" aria-hidden />
          </div>
          <div className="min-w-0 flex-1">
            <h1 className="t-h2">
              Testimonials
            </h1>
            <p className="t-meta">
              {rows.length} total · {pendingCount} pending approval
              {rows.length === 0 ? ' · Users submit after marking a job as hired, or add curated stories below' : ''}
            </p>
          </div>
          <button
            type="button"
            className="ds-btn ds-btn-primary ds-btn-sm shrink-0"
            onClick={() => {
              setAddError(null);
              setAddForm(EMPTY_ADD_FORM);
              setShowAddModal(true);
            }}
          >
            <Plus className="w-3.5 h-3.5" />
            Add testimonial
          </button>
        </div>

        {/* Exa-style toolbar */}
        <div className="flex flex-wrap items-center gap-0 px-0 py-2"
          style={{ borderBottom: '1px solid var(--ds-divider)' }}>
          <div className="flex items-center gap-2">
            <label className="flex items-center gap-1.5 cursor-pointer select-none pl-1">
              <input type="checkbox"
                checked={testSel.size === visible.length && visible.length > 0}
                ref={el => { if (el) el.indeterminate = testSel.size > 0 && testSel.size < visible.length; }}
                onChange={() => testSel.size === visible.length
                  ? setTestSel(new Set())
                  : setTestSel(new Set(visible.map(t => t.id)))}
                className="w-3.5 h-3.5 rounded accent-purple-600"
              />
            </label>
            <span className="t-card-title">
              {visible.length} results
            </span>
            <span className="w-px h-4" style={{ background: 'var(--ds-divider)' }} />
            <button type="button" className="ds-btn ds-btn-ghost ds-btn-sm"><Filter className="w-3.5 h-3.5" /> Filter</button>
            <button type="button" className="ds-btn ds-btn-ghost ds-btn-sm"><ArrowUpDown className="w-3.5 h-3.5" /> Sort</button>
            <span className="w-px h-4" style={{ background: 'var(--ds-divider)' }} />
            {(['pending', 'approved', 'all'] as const).map(f => (
              <button key={f} type="button" onClick={() => { setFilter(f); setTestSel(new Set()); }}
                className="ds-chip ds-chip-button min-h-[44px] touch-manipulation"
                aria-selected={filter === f ? 'true' : undefined}>
                {f === 'pending' ? `Pending (${pendingCount})` : f.charAt(0).toUpperCase() + f.slice(1)}
              </button>
            ))}
          </div>
          <div className="flex-1" />
          <div className="flex items-center gap-1">
            {testSel.size > 0 && (
              <>
                <button type="button" onClick={() => void bulkApprove()}
                  disabled={saving === 'bulk' || visible.filter(t => testSel.has(t.id) && !t.approved).length === 0}
                  className="ds-btn ds-btn-ghost ds-btn-sm" style={{ color: 'var(--ds-ok)' }}>
                  <CheckCircle2 className="w-3.5 h-3.5" /> Approve
                </button>
                <button type="button" onClick={() => void bulkDelete()}
                  disabled={saving === 'bulk'}
                  className="ds-btn ds-btn-ghost ds-btn-sm" style={{ color: 'var(--ds-danger)' }}>
                  <Trash2 className="w-3.5 h-3.5" /> Delete
                </button>
                <span className="ds-chip ds-chip-brand">
                  {testSel.size} selected
                </span>
                <span className="w-px h-4" style={{ background: 'var(--ds-divider)' }} />
              </>
            )}
            <button type="button" onClick={() => void load()} disabled={loading} className="ds-btn ds-btn-ghost ds-btn-sm" title="Refresh">
              <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>

        {error && (
          <div className="flex items-center gap-2 t-meta t-danger">
            <AlertCircle className="w-3.5 h-3.5 shrink-0" />{error}
          </div>
        )}

        <DataTable<Testimonial>
          columns={cols}
          rows={visible}
          loading={loading}
          pageSizeOptions={[10, 25, 50]}
          initialPageSize={10}
          ariaLabel="Testimonials table"
          emptyMessage={
            filter === 'pending'
              ? 'No pending testimonials yet. Users submit after marking a job as hired, or click Add testimonial to curate one.'
              : 'No testimonials found. Click Add testimonial to create your first story.'
          }
        />

        {showAddModal ? (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
            style={{ background: 'rgba(15, 23, 42, 0.45)' }}
            role="dialog"
            aria-modal="true"
            aria-labelledby="add-testimonial-title"
          >
            <div className="w-full max-w-lg rounded-2xl border p-5 space-y-4"
              style={{ background: 'var(--ds-surface, #fff)', borderColor: 'var(--ds-divider)' }}>
              <div className="flex items-start justify-between gap-3">
                <div>
                  <h2 id="add-testimonial-title" className="t-h3">Add testimonial</h2>
                  <p className="t-meta mt-1">
                    Curate a real user story for the landing page. Pending items appear under the Pending tab until approved.
                  </p>
                </div>
                <button
                  type="button"
                  className="ds-btn ds-btn-ghost ds-btn-sm"
                  onClick={() => setShowAddModal(false)}
                  aria-label="Close"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>

              <label className="block space-y-1">
                <span className="t-tag">Display name</span>
                <input
                  className="ds-input w-full"
                  value={addForm.userDisplayName}
                  onChange={(e) => setAddForm((f) => ({ ...f, userDisplayName: e.target.value }))}
                  placeholder="Rahul K."
                />
              </label>

              <label className="block space-y-1">
                <span className="t-tag">Quote ({MIN_QUOTE_LEN}–{MAX_QUOTE_LEN} chars)</span>
                <textarea
                  className="ds-input w-full min-h-[120px]"
                  value={addForm.quote}
                  onChange={(e) => setAddForm((f) => ({ ...f, quote: e.target.value }))}
                  maxLength={MAX_QUOTE_LEN}
                  placeholder="What changed after using GoGrabJob?"
                />
              </label>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <label className="block space-y-1">
                  <span className="t-tag">Role landed</span>
                  <input
                    className="ds-input w-full"
                    value={addForm.roleLanded}
                    onChange={(e) => setAddForm((f) => ({ ...f, roleLanded: e.target.value }))}
                    placeholder="Software Engineer"
                  />
                </label>
                <label className="block space-y-1">
                  <span className="t-tag">Company / location</span>
                  <input
                    className="ds-input w-full"
                    value={addForm.companyName}
                    onChange={(e) => setAddForm((f) => ({ ...f, companyName: e.target.value }))}
                    placeholder="Bengaluru"
                  />
                </label>
              </div>

              <label className="block space-y-1">
                <span className="t-tag">Star rating</span>
                <select
                  className="ds-input w-full"
                  value={addForm.rating}
                  onChange={(e) => setAddForm((f) => ({ ...f, rating: Number(e.target.value) }))}
                >
                  {RATING_OPTIONS.map((r) => (
                    <option key={r} value={r}>
                      {r === 4.5 ? '★★★★½ 4.5 — Great' : r === 4 ? '★★★★☆ 4.0 — Good' : '★★★½☆ 3.5 — Solid'}
                    </option>
                  ))}
                </select>
              </label>

              <label className="flex items-center gap-2 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={addForm.approveImmediately}
                  onChange={(e) => setAddForm((f) => ({ ...f, approveImmediately: e.target.checked }))}
                  className="w-4 h-4 rounded accent-purple-600"
                />
                <span className="t-meta">Publish immediately (skip pending queue)</span>
              </label>

              {addError ? (
                <p className="t-meta t-danger flex items-center gap-1.5">
                  <AlertCircle className="w-3.5 h-3.5 shrink-0" />
                  {addError}
                </p>
              ) : null}

              <div className="flex justify-end gap-2 pt-1">
                <button type="button" className="ds-btn ds-btn-ghost" onClick={() => setShowAddModal(false)}>
                  Cancel
                </button>
                <button
                  type="button"
                  className="ds-btn ds-btn-primary"
                  disabled={saving === 'create'}
                  onClick={() => void handleCreate()}
                >
                  {saving === 'create' ? 'Saving…' : addForm.approveImmediately ? 'Publish' : 'Save as pending'}
                </button>
              </div>
            </div>
          </div>
        ) : null}
      </div>
  );

  if (embedded) return content;
  return (
    <Layout currentPage="testimonials-admin" onNavigate={handleNavigate} onSignOut={onSignOut}>
      {content}
    </Layout>
  );
}
